/**
 * The audio engine.
 *
 * Exactly one `<audio>` element exists for the life of the app. That is not a
 * micro-optimisation — it is what makes a queue work at all:
 *
 * * Browsers grant autoplay permission to a *media element*, not to the page.
 *   Once the user has pressed play on this element, changing its `src` and
 *   calling `play()` again is still "user-initiated", so tracks 2..n in a queue
 *   play without being blocked.
 * * iOS allows only one element to hold the audio focus. Creating a new one per
 *   track loses the lock screen controls and often the audio session with it.
 *
 * So the element lives outside React entirely. React reads state from the store
 * and calls methods here; it never renders the element or owns its lifecycle.
 */

export type EngineEvent =
  | { type: 'timeupdate'; currentTime: number; duration: number }
  | { type: 'durationchange'; duration: number }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'ended' }
  | { type: 'waiting' }
  | { type: 'playing' }
  | { type: 'progress'; buffered: number }
  | { type: 'error'; message: string }

type Listener = (event: EngineEvent) => void

class AudioEngine {
  private element: HTMLAudioElement | null = null
  private listeners = new Set<Listener>()
  private currentUrl: string | null = null

  /**
   * Create the element on demand. Safe to call during render — it only touches
   * the DOM, and the element is never attached to the document, which keeps it
   * out of React's reconciliation entirely.
   */
  private get audio(): HTMLAudioElement {
    if (this.element) return this.element

    const audio = new Audio()
    audio.preload = 'metadata'
    // Streams are same-origin and authenticated by cookie. Setting crossOrigin
    // would turn them into CORS requests and drop the credentials.
    audio.crossOrigin = null

    audio.addEventListener('timeupdate', () =>
      this.emit({
        type: 'timeupdate',
        currentTime: audio.currentTime,
        duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      }),
    )
    audio.addEventListener('durationchange', () =>
      this.emit({
        type: 'durationchange',
        duration: Number.isFinite(audio.duration) ? audio.duration : 0,
      }),
    )
    audio.addEventListener('play', () => this.emit({ type: 'play' }))
    audio.addEventListener('pause', () => this.emit({ type: 'pause' }))
    audio.addEventListener('ended', () => this.emit({ type: 'ended' }))
    audio.addEventListener('waiting', () => this.emit({ type: 'waiting' }))
    audio.addEventListener('playing', () => this.emit({ type: 'playing' }))
    audio.addEventListener('progress', () => {
      const buffered =
        audio.buffered.length > 0 ? audio.buffered.end(audio.buffered.length - 1) : 0
      this.emit({ type: 'progress', buffered })
    })
    audio.addEventListener('error', () => {
      this.emit({ type: 'error', message: describeMediaError(audio.error) })
    })

    this.element = audio
    return audio
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(event: EngineEvent): void {
    for (const listener of this.listeners) listener(event)
  }

  /** Point the element at a new stream. Does not start playback. */
  load(url: string): void {
    if (this.currentUrl === url) return
    this.currentUrl = url
    this.audio.src = url
    this.audio.load()
  }

  async play(): Promise<void> {
    try {
      await this.audio.play()
    } catch (error) {
      // A rejected play() is almost always the autoplay policy, which is not an
      // error worth surfacing — the UI simply stays in the paused state.
      if ((error as DOMException)?.name !== 'NotAllowedError') {
        this.emit({ type: 'error', message: (error as Error)?.message ?? 'Playback failed.' })
      }
      throw error
    }
  }

  pause(): void {
    this.audio.pause()
  }

  seek(seconds: number): void {
    const audio = this.audio
    if (!Number.isFinite(audio.duration)) return
    audio.currentTime = Math.max(0, Math.min(seconds, audio.duration))
  }

  setVolume(volume: number): void {
    this.audio.volume = Math.max(0, Math.min(1, volume))
  }

  setMuted(muted: boolean): void {
    this.audio.muted = muted
  }

  setPlaybackRate(rate: number): void {
    this.audio.playbackRate = rate
  }

  get currentTime(): number {
    return this.element?.currentTime ?? 0
  }

  get duration(): number {
    const value = this.element?.duration ?? 0
    return Number.isFinite(value) ? value : 0
  }

  get paused(): boolean {
    return this.element?.paused ?? true
  }

  /** Release the source. Used on sign-out so the next user starts clean. */
  reset(): void {
    if (!this.element) return
    this.element.pause()
    this.element.removeAttribute('src')
    this.element.load()
    this.currentUrl = null
  }
}

function describeMediaError(error: MediaError | null): string {
  if (!error) return 'Playback failed.'
  switch (error.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was cancelled.'
    case MediaError.MEDIA_ERR_NETWORK:
      return 'The connection dropped while loading this track.'
    case MediaError.MEDIA_ERR_DECODE:
      return 'This audio file could not be decoded.'
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'This audio format is not supported by your browser.'
    default:
      return 'Playback failed.'
  }
}

export const engine = new AudioEngine()
